import React, { useRef, useCallback, useId } from 'react';
import { Paperclip, FileText, XCircle, Loader2, Send, Mic, Video } from 'lucide-react';
import type { Theme, Attachment } from '../../types';
import {
  ALLOWED_FILE_TYPES,
  MAX_INPUT_LENGTH,
  INPUT_WARNING_THRESHOLD,
} from '../../constants/fileUpload';
import { resolveThemeToken, isColorToken } from '../../utils/theme';

interface InputAreaProps {
  input: string;
  setInput: (value: string) => void;
  handleSend: () => void;
  loading: boolean;
  attachments: Attachment[];
  attachmentErrors: string[];
  allowAttachments: boolean;
  allowVoice?: boolean;
  allowVideo?: boolean;
  theme: Theme;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  onFilesSelected: (files: FileList) => void;
  onRemoveAttachment: (id: string) => void;
  placeholder: string;
  onVoiceClick?: () => void;
  onVideoClick?: () => void;
  disabled?: boolean;
  maxInputLength?: number;
  inputWarningThreshold?: number;
  allowedFileTypes?: string[];
}

const InputArea = React.memo(function InputArea({
  input,
  setInput,
  handleSend,
  loading,
  attachments,
  attachmentErrors,
  allowAttachments,
  allowVoice,
  allowVideo,
  theme,
  fileInputRef,
  textAreaRef,
  onFilesSelected,
  onRemoveAttachment,
  placeholder,
  onVoiceClick,
  onVideoClick,
  disabled = false,
  maxInputLength,
  inputWarningThreshold,
  allowedFileTypes,
}: InputAreaProps) {
  const fallbackRef = useRef<HTMLTextAreaElement>(null);
  const taRef = textAreaRef || fallbackRef;
  const inputId = useId();
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-errors`;
  const accept =
    allowedFileTypes && allowedFileTypes.length > 0
      ? allowedFileTypes.join(',')
      : Array.from(ALLOWED_FILE_TYPES).join(',');
  const inputLimit =
    typeof maxInputLength === 'number' && maxInputLength > 0 ? maxInputLength : MAX_INPUT_LENGTH;
  const warningThresholdRaw =
    typeof inputWarningThreshold === 'number' ? inputWarningThreshold : INPUT_WARNING_THRESHOLD;
  const warningThreshold = Math.max(0, Math.min(1, warningThresholdRaw));
  const primary = resolveThemeToken(theme.primary, 'backgroundColor');
  const footerBackground = resolveThemeToken(theme.header || 'bg-white', 'backgroundColor');
  const textColor = resolveThemeToken(theme.text, 'color');
  const hoverClass = isColorToken(theme.hover) ? '' : theme.hover;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.nativeEvent as KeyboardEvent).isComposing) return;
      if (disabled) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        if (loading) return;
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, loading, disabled]
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value.slice(0, inputLimit);
      setInput(value);
      const el = taRef.current;
      if (el) {
        el.style.height = 'auto';
        const max = 160;
        el.style.height = Math.min(el.scrollHeight, max) + 'px';
      }
    },
    [setInput, taRef, inputLimit]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) onFilesSelected(e.target.files);
      e.target.value = '';
    },
    [onFilesSelected]
  );

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const showCounter = input.length >= inputLimit * warningThreshold;

  const describedBy = [helperId, attachmentErrors?.length ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`rw-input-area border-t px-4 py-3 ${footerBackground.className}`}
      style={footerBackground.style}
    >
      {attachmentErrors?.length > 0 && (
        <div id={errorId} role="alert" className="text-xs text-red-600 mb-2">
          {attachmentErrors.map((error, idx) => (
            <div key={`${error}-${idx}`}>{error}</div>
          ))}
        </div>
      )}
      {attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map(att => (
            <div key={att.id} className="relative border rounded p-1">
              {att.previewUrl ? (
                <img
                  src={att.previewUrl}
                  alt={att.name}
                  className="w-16 h-16 object-cover rounded"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="w-16 h-16 flex items-center justify-center text-gray-500">
                  <FileText className="w-5 h-5" aria-hidden="true" />
                </div>
              )}
              <button
                type="button"
                aria-label={`Remove attachment ${att.name}`}
                onClick={() => onRemoveAttachment(att.id)}
                className="absolute -top-2 -right-2 bg-white rounded-full shadow p-0.5 focus:outline-2 focus:outline-blue-500"
              >
                <XCircle className="w-4 h-4 text-gray-600" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {allowAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={accept}
              onChange={handleFileInputChange}
              disabled={disabled}
            />
            <button
              aria-label="Attach files"
              onClick={handleAttachClick}
              type="button"
              disabled={disabled}
              className="p-2 rounded hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
            >
              <Paperclip className="w-4 h-4" aria-hidden="true" />
            </button>
          </>
        )}
        {allowVoice && (
          <button
            aria-label="Start voice input"
            onClick={onVoiceClick}
            type="button"
            disabled={disabled}
            className="p-2 rounded hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
          >
            <Mic className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        {allowVideo && (
          <button
            aria-label="Start video chat"
            onClick={onVideoClick}
            type="button"
            disabled={disabled}
            className="p-2 rounded hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
          >
            <Video className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        <div className="flex-1 relative">
          <textarea
            ref={taRef}
            value={input}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder || 'Message...'}
            rows={1}
            maxLength={inputLimit}
            id={inputId}
            aria-describedby={describedBy}
            className="w-full resize-none rounded-xl border px-3 py-2 text-sm bg-white focus:outline-2 focus:outline-blue-500"
            aria-label="Message input"
            disabled={disabled}
          />
          <span id={helperId} className="sr-only">
            Press Enter to send. Press Shift and Enter for a new line.
          </span>
          {showCounter && (
            <span className="absolute bottom-1 right-2 text-[10px] text-gray-400">
              {input.length}/{inputLimit}
            </span>
          )}
        </div>
        <button
          onClick={handleSend}
          disabled={
            disabled || loading || (!input.trim() && (!attachments || attachments.length === 0))
          }
          aria-label="Send message"
          type="button"
          className={`rounded-full p-2 ${primary.className} ${textColor.className} ${hoverClass} disabled:opacity-50 shadow focus:outline-2 focus:outline-blue-500`}
          style={{ ...primary.style, ...textColor.style }}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
});

export default InputArea;
